import { FC, useMemo } from "react";
import { BubbleMenu, Editor } from "@tiptap/react";
import { Button } from "@src/lib/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import PlusIcon from "@src/lib/icons/18px/plus";
import MinusIcon from "@src/lib/icons/18px/minus";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import { findColumnCountForTable } from "@src/features/libraryItem/course/utils/tipTapTable";
import Text from "@src/lib/ui/Text";

type Props = {
  editor: Editor;
  maxColumns?: number;
};

const TableBubbleMenu: FC<Props> = ({ editor, maxColumns = 10 }) => {
  const columnCount = useMemo(() => {
    const editorState = editor?.state;
    if (editorState) {
      return findColumnCountForTable(editorState);
    }
    return 0;
  }, [editor?.state]);

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{
        duration: 10,
        interactive: true,
        placement: "top",
      }}
      className="flex h-10 items-center gap-1 rounded-full bg-bg-elevated p-1 shadow-out-lg"
      shouldShow={({ editor }) => {
        return editor.isActive("table");
      }}
    >
      <TooltipProvider>
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => {
                  editor.chain().focus().deleteRow().run();
                }}
              >
                <MinusIcon className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Delete row</TooltipContent>
          </Tooltip>
          <Text
            type="P3"
            fontWeight="Medium"
            className="px-1 text-text-secondary"
          >
            Rows
          </Text>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => {
                  editor.chain().focus().addRowAfter().run();
                }}
              >
                <PlusIcon className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Add row</TooltipContent>
          </Tooltip>
        </div>

        <div className="h-5 w-px bg-border-primary" />

        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => {
                  editor.chain().focus().deleteColumn().run();
                }}
              >
                <MinusIcon className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Delete column</TooltipContent>
          </Tooltip>
          <Text
            type="P3"
            fontWeight="Medium"
            className="px-1 text-text-secondary"
          >
            Cols
          </Text>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => {
                  editor.chain().focus().addColumnAfter().run();
                }}
                disabled={columnCount >= maxColumns}
              >
                <PlusIcon className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {columnCount >= maxColumns
                ? `Maximum ${maxColumns} columns`
                : "Add column"}
            </TooltipContent>
          </Tooltip>
        </div>

        <div className="h-5 w-px bg-border-primary" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => {
                editor.chain().focus().deleteTable().run();
              }}
            >
              <TrashOutlineIcon className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Delete table</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </BubbleMenu>
  );
};

export default TableBubbleMenu;
